import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { classNames } from 'tradera/utils/class-names';
import {
    Button as ButtonComponent,
    type ButtonProps
} from 'tradera/components/atoms/button/button';
import { HeaderWithCloseButton } from 'tradera/components/scrollable-modal/header-with-close-button';
import ArrowRight from '@tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import ArrowLeftIcon from 'tradera/blueprint/theme/icons2/arrow-left.svg?jsx';
import styles from './menu-item.module.scss';

type MenuContextType = {
    isOpen: boolean;
    isSlidingOut: boolean;
    closeSubMenu: () => void;
    openSubMenu: () => void;
};

const MenuContext = createContext<MenuContextType | null>(null);

const useMenuContext = () => {
    const context = useContext(MenuContext);
    if (!context) throw new Error('useMenuContext must be used within a Menu');
    return context;
};

type MenuProps = {
    onOpen?: () => void;
    onClose?: () => void;
    /**
     * Menu defaults to being uncontrolled, but it can be controlled by passing this prop.
     */
    isSubMenuOpen?: boolean;
};

/**
 * A menu item is built up of a button and an optional sub menu.
 * If there is a sub menu, the button will open it.
 * A wrapper is needed to hold the state for the menu item.
 *
 * ```tsx
 * <MenuItem.Wrapper>
 *     <MenuItem.Button title="Some item" />
 *     <MenuItem.SubMenu title="Some item">
 *         {children}
 *         <MenuItem.CloseSubMenuButton>Close</MenuItem.CloseSubMenuButton>
 *     </MenuItem.SubMenu>
 * </MenuItem.Wrapper>
 * ```
 */
export const Wrapper = ({
    children,
    onClose,
    onOpen,
    isSubMenuOpen: isOpenProp
}: React.PropsWithChildren<MenuProps>) => {
    const [isOpen, setIsOpen] = useState(isOpenProp ?? false);
    const [isSlidingOut, setSlidingOut] = useState(false);

    const openSubMenu = () => {
        setIsOpen((current) => !current);
        onOpen?.();
    };

    const closeSubMenu = useCallback(() => {
        setSlidingOut(true);
        setTimeout(() => {
            setSlidingOut(false);
            setIsOpen(false);
        }, 400);
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        if (isOpenProp) {
            setIsOpen(true);
        } else if (isOpenProp === false && isOpen) {
            // We need to check if the menu was actually open before closing it,
            // otherwise we may call onClose mehtod when isOpen transitions from undefined to false.
            closeSubMenu();
        }
    }, [isOpenProp, closeSubMenu, isOpen]);

    return (
        <MenuContext.Provider
            value={{ isOpen, closeSubMenu, openSubMenu, isSlidingOut }}>
            {children}
        </MenuContext.Provider>
    );
};

type SubMenuProps = React.PropsWithChildren<{
    title: string;
}>;

export const SubMenu = ({ children, title }: SubMenuProps) => {
    const { isOpen, isSlidingOut, closeSubMenu } = useMenuContext();

    if (!isOpen) return null;

    return (
        <div
            className={classNames(styles.subMenu, {
                [styles.slideOut]: isSlidingOut
            })}>
            <HeaderWithCloseButton
                title={title}
                border
                onClose={() => closeSubMenu()}
                menu={
                    <ButtonComponent
                        theme="text"
                        aria-label="Go back"
                        onClick={() => closeSubMenu()}
                        size="md"
                        icon={<ArrowLeftIcon className="icon" />}
                        round
                    />
                }
            />
            <div className="h-100 overflow-auto">{children}</div>
        </div>
    );
};

type OpenSubMenuButtonProps = Pick<ButtonProps, 'brandedText' | 'onClick'> &
    React.PropsWithChildren<{
        label?: string;
        title?: string;
        icon?: React.ReactNode;
        border?: boolean;
        truncateTitle?: boolean;
        href?: string;
        className?: string;
        brandColorOnHover?: boolean;
    }>;

/**
 * A button that opens a sub menu.
 *
 * If you pass title it will render a default menu item button with arrow.
 * If you want to customize the look you can pass children instead.
 */
export const Button = ({
    label,
    title,
    icon,
    brandedText = true,
    border = true,
    truncateTitle = false,
    children,
    onClick,
    href,
    className,
    brandColorOnHover,
    ...buttonProps
}: OpenSubMenuButtonProps) => {
    const { openSubMenu, isOpen } = useMenuContext();

    return (
        <div
            className={classNames({
                [styles.brandColorOnHover]: brandColorOnHover,
                'border-bottom': border
            })}>
            <ButtonComponent
                theme="unbutton"
                align="left"
                brandedText={brandedText}
                iconLeft={icon}
                fluid
                noTransform
                href={href}
                onClick={(e) => {
                    if (!href) {
                        e.preventDefault();
                        openSubMenu();
                    }
                    onClick?.(e);
                }}
                className={classNames(
                    'd-flex align-items-center gap-x-1 py-2',
                    className,
                    {
                        'text-decoration-none': brandColorOnHover
                    }
                )}
                aria-expanded={isOpen}
                {...buttonProps}>
                {title ? (
                    <>
                        <span
                            className={classNames('flex-grow-1 mr-4', {
                                'text-truncate': truncateTitle
                            })}>
                            {title}
                        </span>
                        {label ? (
                            <span
                                className={classNames(
                                    'text-nowrap font-inter',
                                    {
                                        'text-truncate': !truncateTitle,
                                        'mr-1': icon
                                    }
                                )}>
                                {label}
                            </span>
                        ) : null}
                        <ArrowRight className="icon flex-shrink-0" />
                    </>
                ) : (
                    <>{children}</>
                )}
            </ButtonComponent>
        </div>
    );
};

type CloseButtonProps = ButtonProps & {
    onClick?: () => void;
};

export const CloseSubMenuButton = ({
    children,
    onClick,
    ...buttonProps
}: CloseButtonProps) => {
    const { closeSubMenu } = useMenuContext();

    return (
        <ButtonComponent
            onClick={() => {
                onClick?.();
                closeSubMenu();
            }}
            {...buttonProps}>
            {children}
        </ButtonComponent>
    );
};
